import { ReactNode, useCallback } from 'react'

import { format } from 'date-fns'
import { useTheme } from 'styled-components'

import { CreditCard } from '@assets/icons/credit-card'
import { CheckRounded } from '@assets/icons/material/check-rounded'
import { CCApplePay } from '@assets/thirdParty/cc-apple-pay'
import { GooglePayMark as GooglePay } from '@assets/thirdParty/google-pay-mark'
import {
  ApplePayId,
  GooglePayId,
} from '@components/payment/PaymentButtons/constants'
import { BottomSheet } from '@components/shared/bottom-sheet'
import Dialog from '@components/shared/Dialog'
import {
  SaveButton,
  PaymentMethodItem,
  PaymentMethodLeft,
  PaymentMethodContent,
  CardName,
  CardExpiration,
  SectionHeader,
  PaymentMethodsContainer,
} from '@components/shared/PaymentMethodsModal/styles'
import { PaymentMethod } from '@graphql/queries/paymentMethods'
import { useAvailablePayment } from '@hooks/payment'
import { useStripeContext } from '@services/stripe'

interface PaymentMethodsProps {
  closeModal: () => void
  open: boolean
  addPaymentMethod: () => void
  editPaymentMethod?: (paymentMethod: PaymentMethod) => void
  selectPaymentMethod?: (paymentMethod: PaymentMethod) => void
  confirmSelection?: () => void
  selectedMethod?: PaymentMethod
  selectAMethod?: boolean
  bottomSheet?: boolean
  dialogPortal?: boolean
  hideThirdPartyMethods?: boolean
  confirmLoading?: boolean
  /** Use a higher z-index so this sheet stacks above another (e.g. confirm payment). */
  elevatedZIndex?: boolean
}

export default function PaymentMethodsModal({
  closeModal,
  open,
  addPaymentMethod,
  editPaymentMethod,
  selectAMethod,
  selectedMethod,
  selectPaymentMethod,
  confirmSelection = closeModal,
  bottomSheet = false,
  dialogPortal = false,
  hideThirdPartyMethods = false,
  confirmLoading = false,
  elevatedZIndex = false,
}: PaymentMethodsProps) {
  const { paymentMethods } = useStripeContext()
  const payment = useAvailablePayment()
  const theme = useTheme()

  const renderFooter = useCallback(
    () => (
      <SaveButton
        data-testid="setPaymentMethodButton"
        onClick={confirmSelection}
        loading={confirmLoading}
        fullWidth
      >
        {selectAMethod ? 'Set payment method' : 'Confirm payment methods'}
      </SaveButton>
    ),
    [confirmSelection, selectAMethod, confirmLoading]
  )

  const onSelectMethod = useCallback(
    (paymentMethod: PaymentMethod) => {
      if (selectAMethod) selectPaymentMethod?.(paymentMethod)
      editPaymentMethod?.(paymentMethod)
    },
    [selectAMethod, editPaymentMethod, selectPaymentMethod]
  )

  const renderPaymentMethod = useCallback(
    ({
      hasBorder = false,
      paymentMethod,
      title = '',
      subtitle = '',
      showCheck = false,
      methodImage = <CreditCard />,
    }: {
      hasBorder?: boolean
      paymentMethod: PaymentMethod
      title?: string
      subtitle?: string
      showCheck?: boolean
      methodImage?: ReactNode
    }) => {
      const { id } = paymentMethod
      return (
        <PaymentMethodItem
          $hasBorder={hasBorder}
          key={id}
          onClick={() => onSelectMethod(paymentMethod)}
        >
          <PaymentMethodLeft>
            {methodImage}
            <PaymentMethodContent>
              <CardName>{title}</CardName>
              {Boolean(subtitle) && <CardExpiration>{subtitle}</CardExpiration>}
            </PaymentMethodContent>
          </PaymentMethodLeft>
          {showCheck && (
            <CheckRounded color={theme.palette.brand.primaryBlue} />
          )}
        </PaymentMethodItem>
      )
    },
    [onSelectMethod, theme]
  )

  const renderCardPaymentMethod = useCallback(
    (paymentMethod: PaymentMethod, index: number) => {
      const { id, brand, last4, expirationDate, isDefault } = paymentMethod
      const [year, month, day] = expirationDate.split('-')
      return renderPaymentMethod({
        hasBorder: index !== 0,
        paymentMethod,
        title: `${brand}...${last4}`,
        subtitle: format(
          new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10)
          ),
          'MM/yyyy'
        ),
        showCheck:
          (selectAMethod && selectedMethod?.id === id) ||
          (!selectAMethod && isDefault),
      })
    },
    [selectAMethod, selectedMethod?.id, renderPaymentMethod]
  )

  const Content = (
    <>
      {(paymentMethods?.length > 0 || Boolean(payment)) && (
        <>
          <SectionHeader $margin={10}>Saved</SectionHeader>
          <PaymentMethodsContainer>
            {paymentMethods?.map(renderCardPaymentMethod)}
            {selectAMethod &&
              !hideThirdPartyMethods &&
              payment === 'apple' &&
              renderPaymentMethod({
                hasBorder: paymentMethods.length > 0,
                paymentMethod: {
                  __typename: 'PaymentSource',
                  isDefault: false,
                  id: ApplePayId,
                },
                title: 'Apple Pay',
                showCheck: selectedMethod?.id === ApplePayId,
                methodImage: <CCApplePay width={34} height={30} />,
              })}
            {selectAMethod &&
              !hideThirdPartyMethods &&
              payment === 'google' &&
              renderPaymentMethod({
                hasBorder: paymentMethods.length > 0,
                paymentMethod: {
                  __typename: 'PaymentSource',
                  isDefault: false,
                  id: GooglePayId,
                },
                title: 'Google Pay',
                showCheck: selectedMethod?.id === GooglePayId,
                methodImage: <GooglePay width={40} height={30} />,
              })}
          </PaymentMethodsContainer>
        </>
      )}
      <SectionHeader>Add a payment method</SectionHeader>
      <PaymentMethodLeft onClick={addPaymentMethod} $margin>
        <CreditCard />
        <PaymentMethodContent>
          <CardName data-testid="creditDebitCardButton">
            Credit/Debit Card
          </CardName>
        </PaymentMethodContent>
      </PaymentMethodLeft>
    </>
  )

  if (bottomSheet) {
    return (
      <BottomSheet
        dialogPortal={dialogPortal}
        contentPadding="0 24px 24px"
        showTitleSeparator={false}
        open={open}
        onClose={closeModal}
        footer={renderFooter}
        showFooterSeparator={false}
        secondBottomSheet
        zIndexOffset={elevatedZIndex ? 2 : undefined}
      >
        {Content}
      </BottomSheet>
    )
  }

  return (
    <Dialog
      data-testid="paymentMethodTitle"
      onClose={closeModal}
      open={open}
      header="Payment method"
      closeButton="close"
      footer={renderFooter}
      showFooterSeparator={false}
    >
      {Content}
    </Dialog>
  )
}
